import type { OssVerificationDecision } from '@developerz/domain';
import { createMemo, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { sendOverride } from '../lib/admin-overrides';
import type { AdminOssRequest } from '../lib/adminOss';
import { createConfirmGate } from '../lib/confirm-gate';
import type { OperatorRefusal } from '../lib/operator-credential';
import { ConfirmDialog } from './ConfirmDialog';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { Button } from './ui/Button';

// The decision cell of one admin OSS-verification row (arc 101 slice 07, #68):
// the reviewer note and Approve / Reject, with the state they share. The
// verdict is the REST twin `POST /v1/admin/oss-verifications/:id/decide`
// (`lib/admin-overrides.ts`), whose body requires `confirm: true` — so each
// button ASKS first (`createConfirmGate`) and
// `confirm: true` is sent only from the dialog's action (plan 101 slice 17 rows
// 17-1 / 17-16). It rides an `admin:accounts` operator credential (#5724): a door
// refusal renders `<OperatorRefusalAlert/>` and sends nothing; a refused verdict
// (`{ ok:false }`) is surfaced inline with the API's own text rather than thrown. On success `onDecided` refetches the queue; approving
// also flips the account to the `oss_verified` tier server-side, so the list is
// the truth.

/** Note ceiling — mirrors `OSS_REVIEWER_NOTE_MAX_CHARS` (`@developerz/domain`). */
const NOTE_MAX = 1000;

export function AdminOssDecision(props: {
  row: AdminOssRequest;
  token: string | null;
  onDecided: () => void;
}): JSX.Element {
  const [note, setNote] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);

  const decide = async (decision: OssVerificationDecision): Promise<void> => {
    const reason = note().trim();
    // A rejection the applicant can't read is a dead end: `reviewer_note` is
    // the ONLY thing their panel shows them, so make the reason mandatory.
    if (decision === 'reject' && reason === '') {
      setError(t('admin.oss_note_required'));
      return;
    }
    setBusy(true);
    setError(null);
    setRefusal(null);
    const out = await sendOverride(
      {
        action: 'oss_verify_decide',
        request_id: props.row.id,
        decision,
        ...(reason ? { note: reason } : {}),
        confirm: true,
      },
      props.token,
    );
    setBusy(false);
    if (out.kind !== 'ok') return void setRefusal(out.kind);
    if (!out.value.ok) {
      // The API's own reason, as it came: a missing scope names itself.
      setError(out.value.text);
      return;
    }
    props.onDecided();
  };

  const asking = createConfirmGate<OssVerificationDecision>({ act: decide });
  // The LAST asked verdict, not `pending()`: that goes null the moment the
  // verdict settles, and the dialog stays mounted for its close animation — it
  // would repaint as the other verdict ("Reject …?") on the way out.
  const asked = createMemo<OssVerificationDecision | null>(
    (last) => asking.pending() ?? last,
    null,
  );
  const approving = (): boolean => asked() === 'approve';
  // The note rule is checked BEFORE asking: confirming a reject only to be told
  // the note is missing would be a confirm for nothing.
  const askReject = (): void => {
    if (note().trim() === '') {
      setError(t('admin.oss_note_required'));
      return;
    }
    asking.ask('reject');
  };

  return (
    <>
      <div class="admin-row__controls">
        <Show
          when={props.row.status === 'pending'}
          fallback={
            <p class="admin-row__meta">{props.row.reviewer_note ?? t('admin.oss_no_note')}</p>
          }
        >
          <input
            class="input"
            type="text"
            maxlength={NOTE_MAX}
            disabled={busy()}
            placeholder={t('admin.oss_note_placeholder')}
            aria-label={t('admin.oss_note_label', { repo: props.row.repo_full_name })}
            value={note()}
            onInput={(e) => setNote(e.currentTarget.value)}
          />
          <div class="data-table__actions">
            <Button
              variant="outline"
              size="sm"
              disabled={busy()}
              onClick={() => asking.ask('approve')}
            >
              {t('admin.oss_approve')}
            </Button>
            <Button variant="destructive" size="sm" disabled={busy()} onClick={askReject}>
              {t('admin.oss_reject')}
            </Button>
          </div>
        </Show>
        <OperatorRefusalAlert refusal={refusal()} />
        <Show when={error()}>
          {(msg) => (
            <p class="field__error" role="alert">
              {msg()}
            </p>
          )}
        </Show>
      </div>
      <ConfirmDialog
        open={asking.pending() !== null}
        title={t(approving() ? 'admin.oss_approve_title' : 'admin.oss_reject_title', {
          repo: props.row.repo_full_name,
        })}
        desc={
          approving()
            ? t('admin.oss_approve_desc', { login: props.row.account_login })
            : t('admin.oss_reject_desc')
        }
        cancelLabel={t('admin.confirm_cancel')}
        actionLabel={t(approving() ? 'admin.oss_approve_confirm' : 'admin.oss_reject_confirm')}
        destructive={!approving()}
        onCancel={asking.cancel}
        onConfirm={() => void asking.confirm()}
      />
    </>
  );
}
